'use client'

import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation as useRouterLocation, useNavigate } from 'react-router-dom'
import { Divider } from '@vinted/web-ui'

import {
  GetShippingLabelContextProvider,
  DropOffPointMapContextProvider,
} from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'
import {
  useConversationContext,
  ConversationListContext,
  useConversationSidebarContext,
} from '@marketplace-web/messaging/conversation-providers-feature'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { scrollToTop, useLocation } from '@marketplace-web/shared/browser'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useSession } from '@marketplace-web/shared/session'
import { NotFoundEmptyState } from '@marketplace-web/shared/ui-helpers'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { UiState } from '@marketplace-web/shared/ui-state'
import { ModeratedItemsHeader } from '@marketplace-web/domain/conversation-moderation'
import { MakeOffer, useGetSellerOfferOptions } from '@marketplace-web/offers/price-offers-feature'
import {
  ComposerType,
  INBOX_URL,
  conversationInfoIconClick,
  conversationUserUsernameClick,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { ConversationEducationModal } from '@marketplace-web/conversation-education/conversation-education-feature'
import {
  BlockedUserInfoMessage,
  PhishingFirstTimeListerEducation,
} from '@marketplace-web/domain/conversation-safety'
import { OfflineVerificationFeedback } from '@marketplace-web/domain/conversation-feedback'
import { HttpStatus } from '@marketplace-web/core-api/api-client-util'
import { selfServiceViewScreenEvent } from '_libs/common/event-tracker/events'
import { Screen } from 'constants/tracking/screens'
import { EscrowOrderTrackingEntrySource } from '@marketplace-web/order-details/order-details-data'

import { useBrazeInboxMessageCards } from '@marketplace-web/braze/braze-feature'
import { transformBrazeInboxMessageCardDto } from '@marketplace-web/braze/braze-data'

import Composer from '../Composer'
import ConversationContent from '../ConversationContent'
import ConversationHeader from '../ConversationHeader'
import HeroMessage from '../HeroMessage'
import LoadingFailedEmptyState from '../LoadingFailedEmptyState'
import ConversationLoader from '../ConversationLoader'
import ConversationInfo from '../ConversationDetails'
import { getConversationIdFromUrl, isBrazeConversation } from '../../utils'
import PhishingReminderBanner from '../PhishingReminderBanner'

const Conversation = () => {
  const {
    conversation,
    transaction,
    conversationRequestStatus,
    conversationUiState,
    getConversationRequest,
    transactionUiState,
    setConversation,
    isFirstTimeListerEducationDialogVisible,
    setIsFirstTimeListerEducationDialogVisible,
    setConversationLoadStartForEscrowOrderABTest,
  } = useConversationContext()
  const { toggleSidebar, isSidebarOpen } = useConversationSidebarContext()
  const location = useRouterLocation()
  const conversationIdFromUrl = getConversationIdFromUrl(location.pathname)

  const { inboxMessageCards } = useBrazeInboxMessageCards()

  const brazeConversationCard = useMemo(
    () => inboxMessageCards?.find(card => card.id === conversationIdFromUrl),
    [inboxMessageCards, conversationIdFromUrl],
  )

  const { track } = useTracking()
  const breakpoints = useBreakpoint()
  const navigate = useNavigate()
  const { offer_request, source } = useLocation().searchParams

  const isReplyAllowed = !!(conversation?.isReplyAllowed || conversation?.userHasSupportRole)
  const userId = useSession().user?.id
  const displayedItem = conversation?.item

  const isBlockingImprovementsEnabled = useFeatureSwitch('conversation_blocking_improvements')
  const isNewConversationEducationVisible = useFeatureSwitch('web_new_conversation_education')
  const { conversations, markAsRead, updateListConversationTitle } =
    useContext(ConversationListContext)
  const { sellerOfferOptions } = useGetSellerOfferOptions({
    transactionId: conversation?.transactionId,
    userSide: transaction?.userSide,
  })

  const [showEducation, setShowEducation] = useState(false)
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(!!offer_request)

  useEffect(() => {
    setShowEducation(!!conversation?.education)
  }, [conversation])

  const conversationInList = conversations.find(conv => conversation?.id === conv.id)

  const isLoading =
    !conversation &&
    (!inboxMessageCards || [UiState.Pending, UiState.Idle].includes(conversationUiState))
  const isBraze = !isLoading && !!brazeConversationCard
  const isError = !isLoading && !conversation && conversationUiState === UiState.Failure
  const isConversationNotFound = isError && conversationRequestStatus === HttpStatus.NotFound

  const isPhishingReminderBannerVisible = isReplyAllowed && !isBraze && !!transaction?.userSide

  useEffect(() => {
    if (!isLoading) return

    setConversationLoadStartForEscrowOrderABTest(new Date().getTime())
  }, [setConversationLoadStartForEscrowOrderABTest, isLoading])

  useEffect(() => {
    if (
      !userId ||
      brazeConversationCard ||
      !inboxMessageCards ||
      conversationUiState !== UiState.Idle
    ) {
      return
    }

    const isConversationIdMismatched = String(conversation?.id) !== conversationIdFromUrl

    if (!isConversationIdMismatched) return

    getConversationRequest({ conversationId: conversationIdFromUrl })
  }, [
    getConversationRequest,
    conversation,
    conversationIdFromUrl,
    conversationUiState,
    userId,
    location,
    brazeConversationCard,
    inboxMessageCards,
  ])

  useEffect(() => {
    if (!conversation) return

    if (!conversationInList || isBrazeConversation(conversationInList)) return

    if ('unread' in conversationInList && !conversationInList.unread) return

    markAsRead({ id: conversation.id })
  }, [conversationInList, conversation, markAsRead])

  useEffect(() => {
    if (!breakpoints.portables) return

    scrollToTop()
  }, [breakpoints])

  useEffect(() => {
    if (!brazeConversationCard || conversation?.id === conversationIdFromUrl) return

    const brazeConversation = transformBrazeInboxMessageCardDto(brazeConversationCard)

    setConversation(brazeConversation)
  }, [brazeConversationCard, setConversation, conversationIdFromUrl, conversation?.id])

  const trackViewScreen = useCallback(
    (screen: Screen) => {
      if (!conversation?.transactionId) return

      const event = selfServiceViewScreenEvent({
        screen,
        transactionId: conversation.transactionId,
      })

      track(event)
    },
    [track, conversation?.transactionId],
  )

  useEffect(() => {
    trackViewScreen(Screen.MessageReply)
  }, [trackViewScreen])

  const entrySourceForOrderDetailsTracking = source || EscrowOrderTrackingEntrySource.Other

  const trackViewScreenForOrderDetails = useCallback(() => {
    if (!conversation?.transactionId) return

    const event = selfServiceViewScreenEvent({
      screen: Screen.ConversationEntrySource,
      transactionId: conversation.transactionId.toString(),
      details: JSON.stringify({
        entry_source: entrySourceForOrderDetailsTracking,
        transaction_status: conversation.transaction?.status,
        shipment_status: conversation.transaction?.shipment?.status,
        go_to_order_details_visible:
          !!conversation.orderDetailsVariant && conversation?.orderDetailsVariant !== 'off',
      }),
    })

    track(event)
  }, [track, conversation, entrySourceForOrderDetailsTracking])

  useEffect(() => {
    trackViewScreenForOrderDetails()
  }, [trackViewScreenForOrderDetails])

  if (isConversationNotFound) {
    return <NotFoundEmptyState />
  }

  if (isError) {
    return <LoadingFailedEmptyState />
  }

  if (isLoading) {
    return <ContentLoader testId="conversation-page-loader" />
  }

  if (conversation?.transactionId && transactionUiState !== UiState.Success) {
    return <ContentLoader testId="conversation-page-loader" />
  }

  const handleToggleSidebar = () => {
    toggleSidebar()

    trackViewScreen(Screen.OrderDetails)
  }

  const handleInfoIconClick = () => {
    if (conversation?.transactionId && conversation?.id)
      track(
        conversationInfoIconClick({
          transactionId: conversation?.transactionId,
          conversationId: conversation.id,
          userSide: conversation.transaction?.userSide ?? null,
          oppositeUserId: conversation.oppositeUser?.id ?? null,
        }),
      )

    handleToggleSidebar()
  }

  const handleGoBack = () => {
    navigate(INBOX_URL)
  }

  function closeConversationEducationModal() {
    setShowEducation(false)
  }

  function openOfferModal() {
    setIsOfferModalOpen(true)
  }

  function renderHeroMessage() {
    if (
      !conversation?.transaction?.order ||
      !conversation?.transaction?.offer ||
      !conversation?.transaction?.itemsCount
    ) {
      return null
    }

    return (
      <>
        <HeroMessage toggleSidebar={handleToggleSidebar} openOfferModal={openOfferModal} />
        <Divider />
      </>
    )
  }

  function renderModeratedItemHeader() {
    if (!conversation?.moderatedItems?.itemCount) return null

    return (
      <ModeratedItemsHeader
        moderatedItems={conversation.moderatedItems}
        toggleSidebar={handleToggleSidebar}
      />
    )
  }

  function renderConversationEducationModal() {
    if (!isNewConversationEducationVisible || !conversation?.education) return null

    return (
      <ConversationEducationModal
        conversation={conversation}
        isOpen={showEducation}
        onClose={closeConversationEducationModal}
      />
    )
  }

  function renderBlockedUserIfoMessage() {
    if (!conversation?.oppositeUser?.isHated || !isBlockingImprovementsEnabled) return null

    return (
      <>
        <Divider />
        <BlockedUserInfoMessage
          oppositeUserId={conversation.oppositeUser.id}
          userLogin={conversation.oppositeUser.login}
        />
      </>
    )
  }

  function renderMakeOfferModal() {
    if (!displayedItem) return null

    return (
      <MakeOffer
        isOpen={isOfferModalOpen}
        onClose={() => setIsOfferModalOpen(false)}
        itemId={displayedItem.id}
        itemTitle={transaction?.order.title}
        itemThumbnailUrl={transaction?.order.items[0]?.thumbnailUrl}
        sellerOfferOptions={sellerOfferOptions}
        sellerId={transaction?.seller?.id}
      />
    )
  }

  const handleUsernameClick = () => {
    if (!conversation?.id || !conversation?.transactionId) return

    track(
      conversationUserUsernameClick({
        conversationId: conversation?.id,
        transactionId: conversation?.transactionId,
        userSide: conversation.transaction?.userSide ?? null,
        oppositeUserId: conversation.oppositeUser?.id ?? null,
      }),
    )
  }

  const showMessagesContainer = breakpoints.tabletsUp || (breakpoints.portables && !isSidebarOpen)

  return (
    <GetShippingLabelContextProvider>
      <DropOffPointMapContextProvider shipmentId={transaction?.shipment?.id}>
        <div className="thread__wrapper">
          {showMessagesContainer && (
            <div className="thread__messages-container">
              <ConversationHeader
                oppositeUser={conversation?.oppositeUser}
                onInfoIconClick={handleInfoIconClick}
                onGoBack={handleGoBack}
                onUsernameClick={handleUsernameClick}
                isSystem={isBraze}
              />
              <ConversationLoader />
              <Divider />
              {renderHeroMessage()}
              {renderModeratedItemHeader()}
              <div className="thread__messages-wrapper">
                <div className="thread__messages-main">
                  <ConversationContent
                    toggleSidebar={handleToggleSidebar}
                    openOfferModal={openOfferModal}
                  />
                </div>
              </div>
              {isPhishingReminderBannerVisible && (
                <PhishingReminderBanner threadId={conversation?.id} />
              )}
              {isReplyAllowed && !isBraze && (
                <Composer type={ComposerType.Reply} onPostReply={updateListConversationTitle} />
              )}
              {renderBlockedUserIfoMessage()}
              {renderConversationEducationModal()}
            </div>
          )}
          {isSidebarOpen && (
            <div
              className="thread-sidebar__container thread-sidebar__container--reworked"
              data-testid="sidebar"
            >
              <ConversationInfo toggleSidebar={handleToggleSidebar} />
            </div>
          )}
          <PhishingFirstTimeListerEducation
            isOpen={isFirstTimeListerEducationDialogVisible}
            setIsOpen={setIsFirstTimeListerEducationDialogVisible}
          />
          <OfflineVerificationFeedback />
          {renderMakeOfferModal()}
        </div>
      </DropOffPointMapContextProvider>
    </GetShippingLabelContextProvider>
  )
}

export default Conversation
